import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import {
  paymentApi,
  type PaymentTokenData,
  type PaymentTokenMetadata,
  type PaymentTokenStripeData,
} from '../api/paymentApi';
import { SepaSetupForm } from '../components/SepaSetupForm';
import { CardPaymentForm } from '../components/CardPaymentForm';
import { SepaPaymentForm } from '../components/SepaPaymentForm';
import { PaymentSuccess } from '../components/PaymentSuccess';
import { Loader2, AlertTriangle, ShieldX } from 'lucide-react';
import { resolveLogoUrl } from '@/routes/outil-pro/features/societies/utils/logo.utils';
import env from '@/config/env';

type PageState = 'loading' | 'ready' | 'success' | 'error' | 'expired' | 'used' | 'redirecting-signature';

export function PaymentPage() {
  const { token } = useParams<{ token: string }>();
  const [pageState, setPageState] = useState<PageState>('loading');
  const [tokenData, setTokenData] = useState<PaymentTokenStripeData | null>(null);
  const [errorMessage, setErrorMessage] = useState('');

  // Nettoyer les query params ajoutés par YouSign (signature_request_id, signer_id)
  useEffect(() => {
    if (window.location.search) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    if (!token) {
      setPageState('error');
      setErrorMessage('Lien de paiement invalide.');
      return;
    }

    paymentApi
      .resolveToken(token)
      .then((data: PaymentTokenData) => {
        if (data.requiresSignature) {
          // Le contrat n'est pas encore signé : on renvoie le client sur YouSign.
          // Reproduit le guard Symfony V1 `stripe_payment_link`.
          setPageState('redirecting-signature');
          window.location.replace(data.signatureLink);
          return;
        }
        setTokenData(data);
        setPageState('ready');
      })
      .catch((err) => {
        const message = err?.response?.data?.message || '';
        if (message.includes('expiré')) {
          setPageState('expired');
        } else if (message.includes('déjà été utilisé')) {
          setPageState('used');
        } else {
          setPageState('error');
          setErrorMessage(message || 'Impossible de charger le lien de paiement.');
        }
      });
  }, [token]);

  const handlePaymentSuccess = async () => {
    if (token) {
      try {
        await paymentApi.completePayment(token);
      } catch {
        // Le token est peut-être déjà marqué, on continue
      }
    }
    setPageState('success');
  };

  // Loading
  if (pageState === 'loading') {
    return (
      <div className="tw-min-h-screen tw-bg-gray-50 tw-flex tw-items-center tw-justify-center">
        <div className="tw-text-center">
          <Loader2 className="tw-w-12 tw-h-12 tw-text-affinicar-primary tw-animate-spin tw-mx-auto tw-mb-4" />
          <p className="tw-text-gray-600">Chargement du paiement...</p>
        </div>
      </div>
    );
  }

  // Redirection vers YouSign pour signer avant paiement
  if (pageState === 'redirecting-signature') {
    return (
      <div className="tw-min-h-screen tw-bg-gray-50 tw-flex tw-items-center tw-justify-center">
        <div className="tw-text-center">
          <Loader2 className="tw-w-12 tw-h-12 tw-text-affinicar-primary tw-animate-spin tw-mx-auto tw-mb-4" />
          <p className="tw-text-gray-600">Redirection vers la signature de votre contrat...</p>
        </div>
      </div>
    );
  }

  // Expired
  if (pageState === 'expired') {
    return (
      <div className="tw-min-h-screen tw-bg-gray-50 tw-flex tw-items-center tw-justify-center tw-p-4">
        <div className="tw-bg-white tw-rounded-xl tw-shadow-lg tw-p-8 tw-max-w-md tw-w-full tw-text-center">
          <AlertTriangle className="tw-w-16 tw-h-16 tw-text-yellow-500 tw-mx-auto tw-mb-4" />
          <h1 className="tw-text-xl tw-font-bold tw-text-gray-900 tw-mb-2">Lien expiré</h1>
          <p className="tw-text-gray-600">
            Ce lien de paiement a expiré. Veuillez contacter votre professionnel pour obtenir un nouveau lien.
          </p>
        </div>
      </div>
    );
  }

  // Already used
  if (pageState === 'used') {
    return (
      <div className="tw-min-h-screen tw-bg-gray-50 tw-flex tw-items-center tw-justify-center tw-p-4">
        <div className="tw-bg-white tw-rounded-xl tw-shadow-lg tw-p-8 tw-max-w-md tw-w-full tw-text-center">
          <ShieldX className="tw-w-16 tw-h-16 tw-text-blue-500 tw-mx-auto tw-mb-4" />
          <h1 className="tw-text-xl tw-font-bold tw-text-gray-900 tw-mb-2">Paiement déjà effectué</h1>
          <p className="tw-text-gray-600">
            Ce lien de paiement a déjà été utilisé. Si vous avez une question, contactez votre professionnel.
          </p>
        </div>
      </div>
    );
  }

  // Error
  if (pageState === 'error') {
    return (
      <div className="tw-min-h-screen tw-bg-gray-50 tw-flex tw-items-center tw-justify-center tw-p-4">
        <div className="tw-bg-white tw-rounded-xl tw-shadow-lg tw-p-8 tw-max-w-md tw-w-full tw-text-center">
          <AlertTriangle className="tw-w-16 tw-h-16 tw-text-red-500 tw-mx-auto tw-mb-4" />
          <h1 className="tw-text-xl tw-font-bold tw-text-gray-900 tw-mb-2">Erreur</h1>
          <p className="tw-text-gray-600">{errorMessage}</p>
        </div>
      </div>
    );
  }

  // Success
  if (pageState === 'success') {
    return <PaymentSuccess />;
  }

  // Ready - render the Stripe form
  if (!tokenData) return null;

  const stripePublicKey = tokenData.isDemo && env.STRIPE_TEST_PUBLIC_KEY
    ? env.STRIPE_TEST_PUBLIC_KEY
    : env.STRIPE_PUBLIC_KEY;
  if (!stripePublicKey || stripePublicKey === 'false') {
    return (
      <div className="tw-min-h-screen tw-bg-gray-50 tw-flex tw-items-center tw-justify-center tw-p-4">
        <div className="tw-bg-white tw-rounded-xl tw-shadow-lg tw-p-8 tw-max-w-md tw-w-full tw-text-center">
          <AlertTriangle className="tw-w-16 tw-h-16 tw-text-red-500 tw-mx-auto tw-mb-4" />
          <h1 className="tw-text-xl tw-font-bold tw-text-gray-900 tw-mb-2">Configuration manquante</h1>
          <p className="tw-text-gray-600">Le service de paiement n'est pas configuré.</p>
        </div>
      </div>
    );
  }

  const stripePromise = loadStripe(stripePublicKey, {
    stripeAccount: tokenData.stripeAccountId,
  });

  return (
    <div className="tw-min-h-screen tw-bg-gray-50 tw-flex tw-items-center tw-justify-center tw-p-4">
      <div className="tw-bg-white tw-rounded-xl tw-shadow-lg tw-max-w-lg tw-w-full tw-overflow-hidden">
        {/* Header */}
        <div className="tw-bg-gradient-to-r tw-from-affinicar-primary tw-to-affinicar-secondary tw-px-6 tw-py-5">
          <div className="tw-flex tw-items-center tw-gap-4">
            {tokenData.metadata?.companyLogo && tokenData.metadata?.companyCode && (
              <div className="tw-w-14 tw-h-14 tw-bg-white tw-rounded-full tw-flex tw-items-center tw-justify-center tw-overflow-hidden tw-flex-shrink-0">
                <img
                  src={resolveLogoUrl({
                    code: tokenData.metadata.companyCode,
                    logo: tokenData.metadata.companyLogo,
                  }) ?? undefined}
                  alt={tokenData.metadata.companyName || ''}
                  className="tw-w-full tw-h-full tw-object-contain"
                />
              </div>
            )}
            <div>
              <h1 className="tw-text-xl tw-font-bold tw-text-white tw-mb-1">
                Finaliser votre paiement
              </h1>
              <p className="tw-text-white tw-text-sm tw-opacity-80 tw-mb-0">
                {tokenData.intentType === 'setup_intent'
                  ? 'Mise en place du prélèvement SEPA'
                  : 'Paiement de votre contrat'}
              </p>
            </div>
          </div>
        </div>

        {/* Message contextuel */}
        {tokenData.metadata && (
          <PaymentContextMessage
            metadata={tokenData.metadata}
            intentType={tokenData.intentType}
          />
        )}

        {/* Stripe form */}
        <div className="tw-p-6">
          <Elements
            stripe={stripePromise}
            options={{ clientSecret: tokenData.clientSecret }}
          >
            {tokenData.intentType === 'setup_intent' ? (
              <SepaSetupForm
                clientSecret={tokenData.clientSecret}
                onSuccess={handlePaymentSuccess}
                companyName={tokenData.metadata?.companyName}
                monthlyAmount={tokenData.metadata?.monthlyAmount}
                duration={tokenData.metadata?.duration}
              />
            ) : tokenData.paymentMethod === 'sepa_debit' ? (
              <SepaPaymentForm
                clientSecret={tokenData.clientSecret}
                onSuccess={handlePaymentSuccess}
                sellingPrice={tokenData.metadata?.sellingPrice}
                companyName={tokenData.metadata?.companyName}
              />
            ) : (
              <CardPaymentForm
                clientSecret={tokenData.clientSecret}
                onSuccess={handlePaymentSuccess}
                sellingPrice={tokenData.metadata?.sellingPrice}
                companyName={tokenData.metadata?.companyName}
              />
            )}
          </Elements>
        </div>
      </div>
    </div>
  );
}

function PaymentContextMessage({
  metadata,
  intentType,
}: {
  metadata: PaymentTokenMetadata;
  intentType: 'setup_intent' | 'payment_intent';
}) {
  const vehicle =
    metadata.vehicleBrand && metadata.vehicleModel
      ? `${metadata.vehicleBrand} ${metadata.vehicleModel}`
      : null;

  const isMonthly = intentType === 'setup_intent';

  return (
    <div className="tw-mx-6 tw-mt-6 tw-p-4 tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-lg">
      <div className="tw-text-sm tw-text-gray-700">
        {isMonthly && vehicle && metadata.monthlyAmount && metadata.duration && metadata.totalAmount != null ? (
          <>
            <p>
              Pour finaliser votre contrat mensualisé pour votre{' '}
              <strong>{vehicle}</strong>, merci de remplir les informations ci-dessous.
            </p>
            <p className="tw-mt-2">
              Ce formulaire permet de mettre en place un prélèvement automatique de{' '}
              <strong>{metadata.monthlyAmount.toFixed(2)}&euro;</strong> par mois pendant{' '}
              <strong>{metadata.duration} mois</strong> (soit un total de{' '}
              <strong>{metadata.totalAmount.toFixed(2)}&euro;</strong>
              {metadata.serviceFees != null && metadata.serviceFees > 0 && (
                <> incluant des frais de service de <strong>{metadata.serviceFees.toFixed(2)}&euro;</strong></>
              )}
              ).
            </p>
            <p className="tw-mt-2 tw-mb-0">
              C'est simple, sécurisé, et sans engagement au-delà de la période prévue.
            </p>
          </>
        ) : vehicle && metadata.sellingPrice ? (
          <>
            <p>
              Pour finaliser votre contrat pour votre{' '}
              <strong>{vehicle}</strong>, merci de remplir les informations ci-dessous.
            </p>
            <p className="tw-mt-2">
              Montant à régler : <strong>{metadata.sellingPrice.toFixed(2)}&euro;</strong>.
              Le paiement est sécurisé.
            </p>
          </>
        ) : (
          <p>
            Merci de remplir les informations ci-dessous pour finaliser votre paiement.
            La transaction est sécurisée.
          </p>
        )}
      </div>
    </div>
  );
}
